/* eslint-disable react/prop-types */
import {
  Box,
  Card,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Warning as WarningIcon,
  Category as CategoryIcon,
  LinkOff as LinkOffIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

function PrecautionsCard({
  categories,
  hazardId,
  onUnlinkPrecaution,
  deletingPrecaution,
  onAddPrecaution,
  onAddPrecautionWithCategory,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
          {t("hazards.precautions")}
        </Typography>
        {onAddPrecaution && (
          <Tooltip title={t("hazards.addNewPrecautionTooltip")}>
            <IconButton
              size="small"
              onClick={() => onAddPrecaution(hazardId)}
              sx={{
                color: "success.main",
                bgcolor: alpha(theme.palette.success.main, 0.1),
                "&:hover": {
                  bgcolor: alpha(theme.palette.success.main, 0.2),
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
              aria-label={t("hazards.addNewPrecautionAria")}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ p: 2 }}>
        {categories.map((category, categoryIndex) => (
          <Box key={category.id} sx={{ mb: 2 }}>
            {/* Category Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 1.5,
                p: 1.5,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                border: "1px solid",
                borderColor: alpha(theme.palette.success.main, 0.2),
              }}
            >
              <CategoryIcon
                sx={{
                  color: "success.main",
                  fontSize: 18,
                }}
              />
              <Typography
                variant="body1"
                fontWeight="600"
                color="text.primary"
                sx={{ flex: 1 }}
              >
                {category.name}
              </Typography>
              {onAddPrecautionWithCategory && (
                <Tooltip title={t("hazards.addPrecautionToCategoryTooltip")}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      onAddPrecautionWithCategory(hazardId, category.id)
                    }
                    sx={{
                      color: "success.main",
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.success.main, 0.2),
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.2s ease",
                    }}
                    aria-label={t("hazards.addPrecautionToCategoryAria", {
                      categoryName: category.name,
                    })}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Precautions in this category */}
            {category.precautions?.length > 0 && (
              <List sx={{ p: 0 }}>
                {category.precautions.map((precaution, precautionIndex) => (
                  <Box key={precaution.id}>
                    <Card
                      elevation={1}
                      sx={{
                        mb: 1,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: theme.shadows[4],
                          borderColor: "info.main",
                        },
                      }}
                    >
                      <ListItem sx={{ p: 1.5, py: 1 }}>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.info.main, 0.1),
                              border: "2px solid",
                              borderColor: alpha(theme.palette.info.main, 0.2),
                              width: 32,
                              height: 32,
                              mr: 1,
                            }}
                          >
                            <WarningIcon
                              sx={{
                                color: "info.main",
                                fontSize: 16,
                              }}
                            />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              fontWeight="500"
                              color="text.primary"
                              sx={{ mb: 0 }}
                            >
                              {precaution.name}
                            </Typography>
                          }
                        />
                        <Tooltip title={t("hazards.unlinkPrecautionTooltip")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              onUnlinkPrecaution(hazardId, precaution)
                            }
                            disabled={deletingPrecaution === precaution.id}
                            sx={{
                              color: "warning.main",
                              "&:hover": {
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                              },
                            }}
                            aria-label={t("hazards.unlinkPrecautionAria", {
                              precautionName: precaution.name,
                            })}
                          >
                            {deletingPrecaution === precaution.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <LinkOffIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </ListItem>
                    </Card>
                    {precautionIndex < category.precautions.length - 1 && (
                      <Divider sx={{ my: 0.5, opacity: 0.3 }} />
                    )}
                  </Box>
                ))}
              </List>
            )}
            {categoryIndex < categories.length - 1 && (
              <Divider sx={{ my: 1.5, opacity: 0.4 }} />
            )}
          </Box>
        ))}
      </Box>
    </Card>
  );
}

export default PrecautionsCard;
